/**
 * Normalisation of request failures into text that is safe to show a user
 * Keeps deliberate backend messages, collapses everything else to a generic
 * localised message so provider payloads never reach the screen
 *
 * @public
 * @useWhen Turning a caught request error into a notification or banner
 */

import { i18n } from 'src/CrudManager/Core/Utils/i18n'

/**
 * Shape we read off a rejected request. Kept structural rather than importing
 * `AxiosError` so non-axios rejections go through the same funnel.
 *
 * @internal
 */
interface RequestErrorLike {
  /** Axios transport code (`ERR_NETWORK`, `ECONNABORTED`…) when the call never landed. */
  code?: string
  response?: {
    status?: number
    data?: { message?: unknown }
  }
}

/**
 * Longest backend message still plausible as a sentence written for a human.
 *
 * @internal
 */
const MAX_HUMAN_MESSAGE_LENGTH = 200

/**
 * Backend messages are relayed only when they read as prose meant for the user.
 * Third-party APIs (Google Routes, OpenAI) answer failures with JSON bodies that
 * name the Google Cloud project and the API key, and those bodies have historically
 * been forwarded verbatim inside an exception message. Refusing payload-shaped text
 * here keeps that class of disclosure off the screen even when a backend regresses.
 *
 * @internal
 */
const isHumanReadable = (message: string): boolean => {
  const trimmed = message.trim()

  if (trimmed === '' || trimmed.length > MAX_HUMAN_MESSAGE_LENGTH) return false
  if (/^[[{<]/.test(trimmed)) return false // JSON or HTML body
  if (/[\n\r]/.test(trimmed)) return false // multi-line dump
  if (/"[^"]*"\s*:/.test(trimmed)) return false // serialised key/value pair
  if (/https?:\/\//i.test(trimmed)) return false // internal endpoint or console link

  return true
}

/**
 * Some backends answer with a translation key rather than a sentence. Only those
 * go through i18n: running arbitrary prose through `t()` risks the message compiler
 * choking on braces or `@` that happen to sit in a provider string.
 *
 * @internal
 */
const looksLikeTranslationKey = (message: string): boolean => /^[a-z0-9_]+(\.[a-z0-9_]+)+$/i.test(message)

/**
 * Whether the backend message for this status may be relayed to the user.
 *
 * 4xx bodies are written by this app on purpose — validation, business rules, "not
 * found" — so they are the useful ones. 401/403 come from the framework in English,
 * and 5xx bodies are uncaught exception text, which is where third-party payloads and
 * stack details end up. Those get a generic message instead.
 *
 * @internal
 */
const carriesOwnMessage = (status: number | undefined): boolean =>
  status !== undefined && status >= 400 && status < 500 && status !== 401 && status !== 403

/**
 * Generic message key for a status class, used whenever no message can be trusted.
 *
 * @internal
 */
const genericMessageKey = (error: RequestErrorLike): string => {
  const status = error.response?.status

  // No response at all: blame the connection only when axios says the call never
  // landed, otherwise this is a local failure and "network error" would misinform.
  if (status === undefined) return error.code ? 'crudmanager.errors.networkError' : 'crudmanager.errors.unknown'

  if (status === 401 || status === 403) return 'crudmanager.errors.unauthorized'
  if (status === 404) return 'crudmanager.errors.notFound'
  if (status >= 500) return 'crudmanager.errors.serverError'

  return 'crudmanager.errors.unknown'
}

/**
 * Converts a rejected request into a localised, user-safe message.
 *
 * A 4xx (other than 401/403) carries the app's own validation, business-rule and
 * not-found feedback, so its message is kept when it reads like prose. Everything else
 * collapses to a generic message for its status class — those bodies may quote a
 * third-party API, and axios' own `Request failed with status code 500` is not
 * something to put in front of a user.
 *
 * The function is deliberately side-effect free: log the original error next to the
 * call so the technical detail stays in the browser console for developers.
 *
 * @param error - The caught rejection (axios error, `Error`, or anything else)
 * @returns A localised message safe to display
 * @public
 * @example
 * ```typescript
 * catch (error) {
 *   console.error('Route calculation failed', error)
 *   showErrorNotification(getApiErrorMessage(error))
 * }
 * ```
 */
export const getApiErrorMessage = (error: unknown): string => {
  const requestError = (error ?? {}) as RequestErrorLike
  const rawMessage = requestError.response?.data?.message
  const backendMessage = typeof rawMessage === 'string' ? rawMessage : ''

  if (carriesOwnMessage(requestError.response?.status) && isHumanReadable(backendMessage)) {
    return looksLikeTranslationKey(backendMessage) ? i18n.t(backendMessage) : backendMessage
  }

  return i18n.t(genericMessageKey(requestError))
}
